"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SimpleFuturesProcessor = void 0;
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_quotation_lib_1 = require("trade_quotation_lib");
const trade_utils_lib_1 = require("trade_utils_lib");
const SimpleFuturesAPI_1 = require("../../../apis/SimpleFuturesAPI");
const Calculator_1 = require("../../../component/Calculator");
const FuturesConfig_1 = require("../../../component/FuturesConfig");
const context_1 = require("../../../context");
const hooks_1 = require("../../../hooks");
const interface_1 = require("../../../interface");
const errors_1 = require("../../../interface/errors");
const utils_1 = require("../utils");
const SimpleFuturesTPSLProcessor_1 = require("../SimpleFuturesTPSLProcessor");
const usePositionLimit_1 = require("./usePositionLimit");
const SimpleFuturesLeverageKey = "SimpleFuturesLeverageKey";
const SimpleFuturesInvestKey = "SimpleFuturesInvestKey";
const SimpleFuturesTPSLKey = "SimpleFuturesTPSLKey";
const DefaultInvest = "10";
var SimpleFuturesProcessor;
(function (SimpleFuturesProcessor) {
    /** 交易页面需要的参数 */
    function useTradeUIParams(params) {
        const { symbolInfo, invest, leverage, quoteBalance, isFocused = true, key_id, copyFrom, copyType, tpslParams } = params;
        const feeRate = (0, hooks_1.useFeeRate)(symbolInfo.base, symbolInfo.quote);
        const tradeLimit = useSymbolTradeQuoteLimit(symbolInfo, params.leverage, quoteBalance, isFocused);
        const { min, max } = tradeLimit;
        const TPSLLimit = SimpleFuturesTPSLProcessor_1.SimpleFuturesTPSLProcessor.useTPSLLimit({
            mode: interface_1.SimpleFutures.TPSLMode.simple_rate,
            leverage: leverage,
            feeRate: feeRate,
            isActive: isFocused,
        });
        const TPSLState = SimpleFuturesTPSLProcessor_1.SimpleFuturesTPSLProcessor.useTPSLState(tpslParams, TPSLLimit);
        // 预期购买价值
        const volume = (0, react_1.useMemo)(() => {
            return new trade_utils_lib_1.SafeDecimal(invest).mul(leverage).toString();
        }, [invest, leverage]);
        const avgPriceData = SimpleFuturesProcessor.useOpenOrderAvgPrice({ symbolInfo, volume }, isFocused);
        const liquiPriceData = SimpleFuturesProcessor.useLiquidation({ symbolInfo, avgPriceData, invest, leverage, feeRate });
        const checkAndGetRequest = (0, react_1.useCallback)((params) => {
            const ob = (() => {
                const { side } = params;
                if (!key_id) {
                    return (0, rxjs_1.throwError)(new Error("Login status error"));
                }
                const investD = new trade_utils_lib_1.SafeDecimal(invest);
                if (investD.eq(0)) {
                    return (0, rxjs_1.throwError)(errors_1.TRADE_NO_INVEST);
                }
                if (investD.greaterThan(new trade_utils_lib_1.SafeDecimal(quoteBalance))) {
                    return (0, rxjs_1.throwError)((0, errors_1.cloneError)(errors_1.TRADE_NO_AVAILABLE));
                }
                if (min && investD.lessThan(new trade_utils_lib_1.SafeDecimal(min))) {
                    return (0, rxjs_1.throwError)((0, errors_1.cloneError)(errors_1.TRADE_QUOTE_LOW_THAN_MIN, { min: min }));
                }
                if (max && investD.greaterThan(new trade_utils_lib_1.SafeDecimal(max))) {
                    return (0, rxjs_1.throwError)((0, errors_1.cloneError)(errors_1.TRADE_QUOTE_HIGH_THAN_MAX));
                }
                let tpsl = "";
                if (tpslParams) {
                    if (tpslParams.mode === interface_1.SimpleFutures.TPSLMode.simple_rate) {
                        const params = tpslParams.data;
                        const err = SimpleFuturesTPSLProcessor_1.SimpleFuturesTPSLProcessor.checkTPSLStateError(TPSLState, TPSLLimit);
                        if (err) {
                            return (0, rxjs_1.throwError)(err);
                        }
                        if (params.loss_rate || params.profit_rate) {
                            tpsl = JSON.stringify(tpslParams);
                        }
                    }
                }
                const request = {
                    key_id: key_id,
                    quote: symbolInfo.quote,
                    copy_type: copyType,
                    copy_from: copyFrom,
                    bu_order_data: {
                        quote_invest: invest,
                        portfolio: [
                            {
                                base: symbolInfo.base,
                                leverage: leverage,
                                side: side,
                                mode: "market",
                                tp_sl: tpsl,
                            },
                        ],
                    },
                };
                return (0, rxjs_1.of)(request);
            })();
            return ob.pipe((0, operators_1.catchError)((err) => {
                throw (0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(err);
            }));
        }, [TPSLLimit, TPSLState, invest, key_id, leverage, max, min, quoteBalance, symbolInfo.base, symbolInfo.quote, tpslParams]);
        const openOrder = (0, react_1.useCallback)((params) => {
            return checkAndGetRequest(params).pipe((0, operators_1.mergeMap)((request) => {
                return SimpleFuturesAPI_1.SimpleFuturesAPI.createOrder(request);
            }), (0, operators_1.tap)((res) => {
                utils_1.SimpleFuturesOrderSubject.next(res);
            }));
        }, [checkAndGetRequest]);
        return {
            openOrder: openOrder,
            checkAndGetRequest: checkAndGetRequest,
            tradeLimit: tradeLimit,
            tpslState: TPSLState,
            tpslLimit: TPSLLimit,
            volume: volume,
            avgPriceData: avgPriceData,
            liquiPriceData: liquiPriceData,
        };
    }
    SimpleFuturesProcessor.useTradeUIParams = useTradeUIParams;
    /** 订阅市价预估开仓均价 volume为预期买入量 */
    const OBMaxLength = 150;
    const OBLengthPerTime = 50;
    function useOpenOrderAvgPrice(params, isActive) {
        const { symbolInfo, volume, side } = params;
        const [obLength, setLength] = (0, react_1.useState)(OBLengthPerTime);
        const [result, setResult] = (0, react_1.useState)();
        // 交易对变动时重置长度
        (0, react_1.useEffect)(() => {
            setLength(OBLengthPerTime);
        }, [symbolInfo.symbol, volume]);
        // 交易对变动时重置结果
        (0, react_1.useEffect)(() => {
            setResult(undefined);
        }, [symbolInfo.symbol]);
        (0, react_1.useEffect)(() => {
            if (!volume || !Number(volume) || !isActive) {
                return;
            }
            // 大于150档不取了
            if (obLength > OBMaxLength) {
                setResult(undefined);
                return;
            }
            const sub = (0, trade_quotation_lib_1.getGlobalQuotationSocketMgr)()
                .getOrderBookV2WebSocket()
                .getOrderBookObservable({
                market: "pionex.v2",
                base: symbolInfo.base,
                quote: symbolInfo.quote,
                limit: Math.min(OBMaxLength, obLength),
            })
                .pipe((0, operators_1.throttleTime)(1000), (0, operators_1.filter)((data) => {
                // 检测长度是否足够
                if (!side || side === interface_1.CustomOrderSide.BUY) {
                    // 买的话要吃卖盘
                    const asks = data.asks;
                    // 取ob中最大成交额
                    if (asks.length) {
                        const asksVolumeSum = asks[asks.length - 1].accVolume;
                        if (asksVolumeSum < Number(volume)) {
                            setLength((prevState) => {
                                return prevState + OBLengthPerTime;
                            });
                            return false;
                        }
                    }
                }
                if (!side || side === interface_1.CustomOrderSide.SELL) {
                    // 卖的话要吃买盘
                    const bids = data.bids;
                    // 取ob中最大成交额
                    if (bids.length) {
                        const bidsVolumeSum = bids[bids.length - 1].accVolume;
                        if (bidsVolumeSum < Number(volume)) {
                            setLength((prevState) => {
                                return prevState + OBLengthPerTime;
                            });
                            return false;
                        }
                    }
                }
                return true;
            }), (0, operators_1.map)((data) => {
                let asksAmountSum = 0;
                let asksVolumeSum = 0;
                let bidsAmountSum = 0;
                let bidsVolumeSum = 0;
                // 检测长度是否足够
                if (!side || side === interface_1.CustomOrderSide.BUY) {
                    // 买的话要吃卖盘
                    const asks = data.asks;
                    for (let i = 0; i < asks.length; i++) {
                        const item = asks[i];
                        // 还剩多少投资额没加
                        const remainderValue = Number(volume) - asksVolumeSum;
                        if (remainderValue <= 0) {
                            break;
                        }
                        // 如果剩余没加的交易额大于该档的交易额，直接赋值从卖1到该档的总交易额
                        // 反之,加剩余的 结束遍历
                        if (remainderValue > item.volume) {
                            asksVolumeSum = item.accVolume;
                            asksAmountSum = item.accQuantity;
                        }
                        else {
                            asksVolumeSum = asksVolumeSum + remainderValue;
                            asksAmountSum = asksAmountSum + (remainderValue / item.volume) * item.quantity;
                            break;
                        }
                    }
                }
                if (!side || side === interface_1.CustomOrderSide.SELL) {
                    // 卖的话要吃买盘
                    const bids = data.bids;
                    for (let i = 0; i < bids.length; i++) {
                        const item = bids[i];
                        // 还剩多少投资额没加
                        const remainderValue = Number(volume) - bidsVolumeSum;
                        if (remainderValue <= 0) {
                            break;
                        }
                        // 如果剩余没加的交易额大于该档的交易额，直接赋值从卖1到该档的总交易额
                        // 反之,加剩余的 结束遍历
                        if (remainderValue > item.volume) {
                            bidsVolumeSum = item.accVolume;
                            bidsAmountSum = item.accQuantity;
                        }
                        else {
                            bidsVolumeSum = bidsVolumeSum + remainderValue;
                            bidsAmountSum = bidsAmountSum + (remainderValue / item.volume) * item.quantity;
                            break;
                        }
                    }
                }
                const buyAvgPrice = asksAmountSum ? asksVolumeSum / asksAmountSum : 0;
                const sellAvgPrice = bidsAmountSum ? bidsVolumeSum / bidsAmountSum : 0;
                return {
                    buyAvgPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(buyAvgPrice, symbolInfo.precision, trade_utils_lib_1.SafeDecimal.ROUND_CEIL),
                    sellAvgPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(sellAvgPrice, symbolInfo.precision),
                };
            }))
                .subscribe({
                next: setResult,
                error: (err) => {
                    console.log(`Error: useOpenOrderAvgPrice: ${err} `);
                },
            });
            return () => {
                sub.unsubscribe();
            };
        }, [isActive, obLength, side, symbolInfo.base, symbolInfo.precision, symbolInfo.quote, volume]);
        return result;
    }
    SimpleFuturesProcessor.useOpenOrderAvgPrice = useOpenOrderAvgPrice;
    /** 关单 */
    function closeOrder(id) {
        return SimpleFuturesAPI_1.SimpleFuturesAPI.closeOrder({ bu_order_id: id, mode: "market" });
    }
    SimpleFuturesProcessor.closeOrder = closeOrder;
    /** 关单所有的订单 */
    function closeAllOrder(data) {
        return SimpleFuturesAPI_1.SimpleFuturesAPI.closeAllOrder(data);
    }
    SimpleFuturesProcessor.closeAllOrder = closeAllOrder;
    /** 获取当前场景的最大最小投资额 */
    function useSymbolTradeQuoteLimit(symbolInfo, leverage, balance = "", isActive = true) {
        var _a, _b;
        const ticker = (0, context_1.useFuturesTickerParseContext)({
            base: symbolInfo.base,
            quote: symbolInfo.quote,
            isFocused: isActive,
        });
        const leverageConfig = (0, FuturesConfig_1.useLeverageConfigRow)(symbolInfo.symbol, leverage);
        const baseLimit = (_a = symbolInfo.limit) === null || _a === void 0 ? void 0 : _a.dumping;
        const quoteLimit = (_b = symbolInfo.limit) === null || _b === void 0 ? void 0 : _b.spending;
        return (0, react_1.useMemo)(() => {
            let max = undefined;
            if (baseLimit === null || baseLimit === void 0 ? void 0 : baseLimit.max) {
                max = new trade_utils_lib_1.SafeDecimal(baseLimit.max).mul(new trade_utils_lib_1.SafeDecimal(ticker === null || ticker === void 0 ? void 0 : ticker.latest)).div(leverage).toString();
                if (quoteLimit === null || quoteLimit === void 0 ? void 0 : quoteLimit.max) {
                    max = trade_utils_lib_1.SafeDecimal.min(max, new trade_utils_lib_1.SafeDecimal(quoteLimit.max).div(leverage)).toString();
                }
            }
            if (leverageConfig.curLeverageRow) {
                if (max) {
                    max = trade_utils_lib_1.SafeDecimal.min(new trade_utils_lib_1.SafeDecimal(max), new trade_utils_lib_1.SafeDecimal(leverageConfig.curLeverageRow.notional_limit).div(leverage)).toString();
                }
                else {
                    max = leverageConfig.curLeverageRow.notional_limit;
                }
            }
            if (Number(balance || 0)) {
                if (max) {
                    max = trade_utils_lib_1.SafeDecimal.min(new trade_utils_lib_1.SafeDecimal(max), new trade_utils_lib_1.SafeDecimal(balance)).toString();
                }
                else {
                    max = balance;
                }
            }
            let min = undefined;
            if (baseLimit === null || baseLimit === void 0 ? void 0 : baseLimit.min) {
                // base 的最小，乘以2来做冗余
                min = new trade_utils_lib_1.SafeDecimal(baseLimit.min).mul(2).mul(new trade_utils_lib_1.SafeDecimal(ticker === null || ticker === void 0 ? void 0 : ticker.latest)).div(leverage).toString();
                if (quoteLimit === null || quoteLimit === void 0 ? void 0 : quoteLimit.min) {
                    min = trade_utils_lib_1.SafeDecimal.max(min, new trade_utils_lib_1.SafeDecimal(quoteLimit.min).div(leverage)).toString();
                }
            }
            return {
                min: min ? trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(min, symbolInfo.precisionCost, trade_utils_lib_1.SafeDecimal.ROUND_CEIL) : undefined,
                max: max ? trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(max, symbolInfo.precisionCost, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR) : undefined,
            };
        }, [baseLimit, leverageConfig.curLeverageRow, balance, symbolInfo.precisionCost, ticker === null || ticker === void 0 ? void 0 : ticker.latest, leverage, quoteLimit]);
    }
    SimpleFuturesProcessor.useSymbolTradeQuoteLimit = useSymbolTradeQuoteLimit;
    // 计算强平价
    function useLiquidation(params) {
        var _a;
        const { symbolInfo, avgPriceData, invest, leverage, feeRate } = params;
        const configData = (0, FuturesConfig_1.useLeverageConfig)(symbolInfo.symbol);
        return (0, react_1.useMemo)(() => {
            var _a, _b;
            if (!avgPriceData) {
                return;
            }
            // 杠杆倍数
            const leverageD = new trade_utils_lib_1.SafeDecimal(leverage);
            // 多仓开仓均价
            const longAvg = new trade_utils_lib_1.SafeDecimal(avgPriceData.buyAvgPrice);
            // 空仓开仓均价
            const shortAvg = new trade_utils_lib_1.SafeDecimal(avgPriceData.sellAvgPrice);
            // 本金
            const investD = new trade_utils_lib_1.SafeDecimal(invest);
            // 仓位价值
            const positionValue = investD.mul(leverageD);
            // 预计多仓仓位
            const longQty = positionValue.div(longAvg);
            // 预计空仓仓位
            const shortQty = positionValue.div(shortAvg);
            // 手续费
            const fee = investD.mul(leverage).mul(new trade_utils_lib_1.SafeDecimal(feeRate === null || feeRate === void 0 ? void 0 : feeRate.takerFeeRate));
            const longLiquiPrice = Calculator_1.LiquidationCalculator.calcuLiquidationPrice({
                leverageConfigs: ((_a = configData.leverageConfig) === null || _a === void 0 ? void 0 : _a.rows) || [],
                position: longQty.toString(),
                avgPrice: longAvg.toString(),
                side: "long",
                assets: investD.toString(),
                otherSpending: fee.toString(),
            });
            const shortLiquiPrice = Calculator_1.LiquidationCalculator.calcuLiquidationPrice({
                leverageConfigs: ((_b = configData.leverageConfig) === null || _b === void 0 ? void 0 : _b.rows) || [],
                position: shortQty.toString(),
                avgPrice: shortAvg.toString(),
                side: "short",
                assets: investD.toString(),
                otherSpending: fee.toString(),
            });
            return {
                longLiquiPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(longLiquiPrice, symbolInfo.precision, trade_utils_lib_1.SafeDecimal.ROUND_CEIL),
                shortLiquiPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(shortLiquiPrice, symbolInfo.precision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR),
            };
        }, [avgPriceData, (_a = configData.leverageConfig) === null || _a === void 0 ? void 0 : _a.rows, feeRate === null || feeRate === void 0 ? void 0 : feeRate.takerFeeRate, invest, leverage, symbolInfo.precision]);
    }
    SimpleFuturesProcessor.useLiquidation = useLiquidation;
    /** 设置默认的杠杆倍数 */
    function setLocalDefaultLeverage(value) {
        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesLeverageKey, `${value}`);
    }
    SimpleFuturesProcessor.setLocalDefaultLeverage = setLocalDefaultLeverage;
    /** 获取当前配置的杠杆倍数 */
    function useLeverage(symbolInfo, onLeverageExceeded) {
        const [leverage, setLeverage] = (0, react_1.useState)(10);
        // 初始化杠杆倍数
        (0, react_1.useEffect)(() => {
            (0, trade_utils_lib_1.getLocalStorage)()
                .get(SimpleFuturesLeverageKey, "10")
                .pipe((0, operators_1.map)((res) => {
                return new trade_utils_lib_1.SafeDecimal(res).toNumber();
            }))
                .subscribe({
                next: setLeverage,
                error: () => { },
            });
        }, []);
        // 获取该币对杠杆倍数配置
        const leverageConfig = (0, FuturesConfig_1.useLeverageConfigRow)(symbolInfo.symbol);
        // 当默认杠杆倍数大约最大杠杆倍数时做处理
        (0, react_1.useEffect)(() => {
            if (leverageConfig.realMaxLeverage && leverage > leverageConfig.realMaxLeverage) {
                onLeverageExceeded(leverageConfig.realMaxLeverage);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [leverageConfig.realMaxLeverage]);
        const parseLeverage = (0, react_1.useMemo)(() => {
            if (leverage > leverageConfig.maxLeverage) {
                return leverageConfig.maxLeverage;
            }
            return leverage;
        }, [leverage, leverageConfig.maxLeverage]);
        return [parseLeverage, setLeverage];
    }
    SimpleFuturesProcessor.useLeverage = useLeverage;
    /** 设置默认的投资额 */
    function setLocalDefaultInvest(value) {
        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesInvestKey, value);
    }
    SimpleFuturesProcessor.setLocalDefaultInvest = setLocalDefaultInvest;
    /** 获取当前默认的投资额 */
    function useInvest() {
        const [invest, setInvest] = (0, react_1.useState)(DefaultInvest);
        // 初始化杠杆倍数
        (0, react_1.useEffect)(() => {
            (0, trade_utils_lib_1.getLocalStorage)()
                .get(SimpleFuturesInvestKey, DefaultInvest)
                .subscribe({
                next: (value) => {
                    setInvest(value !== null && value !== void 0 ? value : DefaultInvest);
                },
                error: () => { },
            });
        }, []);
        return [invest, setInvest];
    }
    SimpleFuturesProcessor.useInvest = useInvest;
    /** 设置默认的投资额 */
    function setLocalDefaultTPSL(value) {
        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesTPSLKey, JSON.stringify(value));
    }
    SimpleFuturesProcessor.setLocalDefaultTPSL = setLocalDefaultTPSL;
    /** 获取当前默认的投资额 */
    function useTPSL() {
        const [TPSL, setTPSL] = (0, react_1.useState)();
        // 初始化杠杆倍数
        (0, react_1.useEffect)(() => {
            (0, trade_utils_lib_1.getLocalStorage)()
                .get(SimpleFuturesTPSLKey)
                .pipe((0, operators_1.map)((res) => {
                return res ? JSON.parse(res) : null;
            }))
                .subscribe({
                next: (value) => {
                    if (value) {
                        setTPSL(value);
                    }
                },
                error: () => { },
            });
        }, []);
        return [TPSL, setTPSL];
    }
    SimpleFuturesProcessor.useTPSL = useTPSL;
    /** 充值/提取保证金 */
    SimpleFuturesProcessor.transferMargin = (params) => {
        return SimpleFuturesAPI_1.SimpleFuturesAPI.transferProfit(Object.assign(Object.assign({}, params), { type: "margin" }));
    };
    SimpleFuturesProcessor.usePositionLimit = usePositionLimit_1.usePositionLimit;
})(SimpleFuturesProcessor = exports.SimpleFuturesProcessor || (exports.SimpleFuturesProcessor = {}));
